import { useState } from 'react';
import { Navbar } from '../home/components/Navbar';
import { Footer } from '../home/components/Footer';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'How long does it take to receive my custom shirt?',
    answer: 'Most orders are printed and ready within 48 hours. Shipping typically takes 3-5 business days. If you\'re local to Starkville, you can pick up your order in-store for same-day or next-day service!'
  },
  {
    question: 'Can I customize any design I see on your site?',
    answer: 'Absolutely! Every shirt on our site can be customized. Click "Customize This" on any product to open our design studio where you can change colors, add text, upload graphics, and make it uniquely yours.'
  },
  {
    question: 'What file types can I upload for custom graphics?',
    answer: 'We accept PNG, JPG, and SVG files up to 10MB. For best print quality, we recommend high-resolution images (at least 300 DPI) with transparent backgrounds. PNG files work best for logos and graphics.'
  },
  {
    question: 'Do you offer bulk discounts for team orders?',
    answer: 'Yes! We offer special pricing for orders of 10+ shirts. Contact us at hello@ontargetprints.com with your design details and quantity, and we\'ll provide a custom quote within 24 hours.'
  },
  {
    question: 'What shirt brands and styles do you use?',
    answer: 'We use premium quality brands including Bella+Canvas, Gildan, and Next Level. All shirts are pre-shrunk, soft cotton or cotton blends. You can choose from crew neck, v-neck, long sleeve, and tank top styles.'
  },
  {
    question: 'How do I know what size to order?',
    answer: 'Each product page includes a detailed size chart. Our shirts run true to size. If you\'re between sizes or prefer a looser fit, we recommend sizing up. Contact us if you need help choosing!'
  },
  {
    question: 'What is your return and exchange policy?',
    answer: 'We want you to love your custom shirt! If there\'s a printing error or defect, we\'ll reprint it for free. Since each shirt is custom-made, we can\'t accept returns for buyer\'s remorse, but we\'ll work with you to make it right.'
  },
  {
    question: 'Can I see a proof before my shirt is printed?',
    answer: 'Our design studio shows you a live preview as you customize. What you see is what you get! If you need additional approval for a complex design or bulk order, contact us and we can send a digital proof.'
  },
  {
    question: 'Do you ship outside of Mississippi?',
    answer: 'Yes! We ship anywhere in the United States. Shipping is free on orders over $50. International shipping is not currently available, but we\'re working on it!'
  },
  {
    question: 'How do I care for my custom printed shirt?',
    answer: 'Turn your shirt inside out and wash in cold water. Tumble dry on low or hang dry. Avoid bleach and ironing directly on the design. Following these steps will keep your print looking fresh for years!'
  }
];

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#6B0000] to-[#4A0000]">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 bg-white/10 text-white text-xs font-bold uppercase tracking-widest rounded-full mb-6">
            Help Center
          </span>
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Everything you need to know about custom shirts and ordering
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 flex items-center justify-between gap-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-black text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-red-50 rounded-lg">
                    <i
                      className={`ri-arrow-down-s-line text-xl text-[#6B0000] transition-transform duration-300 ${
                        openIndex === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </div>
                </button>
                
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-8 pb-6 pt-2">
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Still Have Questions */}
          <div className="mt-16 bg-gradient-to-br from-[#6B0000] to-[#4A0000] rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-black text-white mb-4">
              Still Have Questions?
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-xl mx-auto">
              Can't find what you're looking for? Our team is here to help!
            </p>
            <a
              href="/contact"
              className="inline-block px-8 py-4 bg-white text-[#6B0000] text-sm font-bold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}